import BiMap from 'bidirectional-map';

import { Block, type Document } from '@gitbook/slate';
import { normalizeURLAnchor } from '@gitbook/util-url';

import * as expandable from '../expandable';
import {
    BLOCK_EXPANDABLE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_TABS_ITEM,
    BLOCK_UPDATE,
} from '../types';
import { isSectionDelimiter, type SectionDelimiterBlock } from './isSectionDelimiter';

/**
 * Mapping of block keys to ID for url.
 */
export type SectionIDs = BiMap<string>;

// Cache to optimize multiple calls to getSectionIDs when rendering the application.
const sectionIdsCache = new WeakMap<Document | Block, SectionIDs>();

/**
 * Create an empty SectionIDs.
 */
export function createSectionIDs(): SectionIDs {
    return new BiMap<string>();
}

/**
 * Create a mapping of ID for headers (key => id). Pass an existing SectionIDs and, if there
 * are no changes, the same instance is returned.
 */
export function getSectionIDs(document: Document, existing: SectionIDs = new BiMap()): SectionIDs {
    const inCache = sectionIdsCache.get(document);
    if (inCache) {
        return inCache;
    }

    const ids = createSectionIDs();
    const nextSuffixByBaseId = new Map<string, number>();

    const allocateIdForNode = (node: SectionDelimiterBlock) => {
        // Explicit user-chosen IDs are preserved as written; only anchors derived
        // from text get the ad-blocker rewrite.
        const baseID = normalizeURLAnchor(getNodeTextForID(node), {
            rewriteAdPatterns: !hasExplicitID(node),
        });
        const ID = getNextAvailableID(ids, baseID, nextSuffixByBaseId);
        ids.set(node.key, ID);
    };

    const pendingNodes: Block[] = [];
    document.nodes.forEach((node) => pendingNodes.push(node));
    pendingNodes.reverse();

    while (pendingNodes.length > 0) {
        const node = pendingNodes.pop() as Block;
        if (!node.isBlock()) {
            continue;
        }

        if (isSectionDelimiter(node)) {
            allocateIdForNode(node);
        }

        const descendants: Block[] = [];
        if (Block.isBlock(node.nodes.first())) {
            node.blockNodes.forEach((child) => descendants.push(child));
        }
        node.fragments.forEach((fragment) => {
            fragment.nodes.forEach((child) => descendants.push(child));
        });

        for (let index = descendants.length - 1; index >= 0; index--) {
            pendingNodes.push(descendants[index]);
        }
    }

    // Re-use reference if there are no changes
    // it optimizes the rendering of the application
    const result = existing && areSectionIDsEqual(ids, existing) ? existing : ids;
    sectionIdsCache.set(document, result);

    return result;
}

/**
 * Helper for getSectionIDs, find the next available id if one is already taken.
 */
function getNextAvailableID(
    sectionIDs: SectionIDs,
    baseID: string,
    nextSuffixByBaseId: Map<string, number>
): string {
    let suffix = nextSuffixByBaseId.get(baseID) ?? 0;
    let id = suffix > 0 ? `${baseID}-${suffix}` : baseID;

    while (sectionIDs.hasValue(id)) {
        suffix++;
        id = `${baseID}-${suffix}`;
    }

    nextSuffixByBaseId.set(baseID, suffix + 1);
    return id;
}

/**
 * Get the text to compute the best looking ID for a node.
 */
function hasExplicitID(node: SectionDelimiterBlock): boolean {
    switch (node.type) {
        case BLOCK_HEADING_1:
        case BLOCK_HEADING_2:
        case BLOCK_HEADING_3:
            return !!node.data.get('id');
        default:
            return false;
    }
}

export function getNodeTextForID(node: SectionDelimiterBlock): string {
    switch (node.type) {
        case BLOCK_OPENAPI: {
            if (node.data.has('ref')) {
                return `${node.data.get('method').toUpperCase()} ${node.data.get('path')}`;
            }
            return node.data.get('summary') || '';
        }
        case BLOCK_OPENAPI_OPERATION:
            return `${node.data.get('method').toUpperCase()} ${node.data.get('path')}`;
        case BLOCK_OPENAPI_SCHEMAS: {
            const title = node.data.get('title');
            if (title) {
                return title;
            }
            const schemas = node.data.get('schemas');
            const schema = schemas[0];
            if (schemas.length === 1 && schema) {
                return `The ${schema} object`;
            }
            return '';
        }
        case BLOCK_OPENAPI_WEBHOOK: {
            return node.data.has('ref') ? node.data.get('name') : '';
        }
        case BLOCK_EXPANDABLE: {
            return expandable.getTitleFragment(node).text;
        }
        case BLOCK_TABS_ITEM: {
            return node.data.get('title') || '';
        }
        case BLOCK_UPDATE:
            return node.data.get('date') || '';
        case BLOCK_HEADING_1:
        case BLOCK_HEADING_2:
        case BLOCK_HEADING_3:
            return node.data.get('id') || node.text || '';
        default:
            return '';
    }
}

/**
 * Returns true if the two given HeadingIDs are equal.
 */
function areSectionIDsEqual(lhs: SectionIDs, rhs: SectionIDs): boolean {
    if (lhs.size !== rhs.size) {
        return false;
    }

    for (const lhsKey of lhs.keys()) {
        if (rhs.get(lhsKey) !== lhs.get(lhsKey)) {
            return false;
        }
    }

    return true;
}
